// ClientEntitiesHub — merged Entities & Roles + Family/Entity Matrix.
// Two tabs over the same data: List (existing ClientEntities) and Matrix view.
// Deep-link supported via ?view=matrix.
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Building2, GitBranch, ImageIcon, Pencil } from 'lucide-react';
import ClientEntities from './ClientEntities';
import ClientMatrix from './ClientMatrix';
import MatrixChartTab from './MatrixChartTab';
import MatrixEditor from './MatrixEditor';

const TABS = [
  { id: 'list', label: 'Entities & Roles', icon: Building2 },
  { id: 'matrix', label: 'Auto Matrix', icon: GitBranch },
  { id: 'editor', label: 'Interactive Editor', icon: Pencil },
  { id: 'chart', label: 'Uploaded Chart', icon: ImageIcon },
];

export default function ClientEntitiesHub() {
  const [params, setParams] = useSearchParams();
  const fromUrl = (params.get('view') || '').toLowerCase();
  const initial = TABS.some((t) => t.id === fromUrl) ? fromUrl : 'list';
  const [tab, setTab] = useState(initial);

  useEffect(() => {
    const next = (params.get('view') || '').toLowerCase();
    const valid = TABS.some((t) => t.id === next) ? next : 'list';
    if (valid !== tab) setTab(valid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleTab = (id) => {
    setTab(id);
    if (id === 'list') params.delete('view'); else params.set('view', id);
    setParams(params, { replace: true });
  };

  return (
    <div className="animate-fade-in-up" data-testid="entities-hub">
      <div className="flex items-center gap-1 border-b border-[#E6E0D9] mb-4" data-testid="entities-hub-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => handleTab(t.id)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 -mb-px transition-colors',
                active
                  ? 'border-[#4A3B32] text-[#2C2420] font-medium'
                  : 'border-transparent text-[#5D524D] hover:text-[#2C2420]'
              )}
              data-testid={`entities-hub-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>
      {tab === 'chart' && <MatrixChartTab />}
      {tab === 'editor' && <MatrixEditor />}
      {tab === 'matrix' && <ClientMatrix />}
      {tab === 'list' && <ClientEntities />}
    </div>
  );
}
